// Packages Module
import { api } from '../core/api-client.js';
import { showNotification } from '../utils/notifications.js';
import { hasPermission } from '../utils/permissions.js';

export async function loadPackages() {
    if (!hasPermission('view:settings')) {
        console.log('[Packages] No permission to view packages');
        return;
    }

    try {
        let packages = await api.getPackages();
        console.log('[Packages] Raw response:', packages);

        // Normalize response
        if (!Array.isArray(packages)) {
            if (packages.data && Array.isArray(packages.data)) {
                packages = packages.data;
            } else if (packages.packages && Array.isArray(packages.packages)) {
                packages = packages.packages;
            } else {
                packages = [];
            }
        }

        console.log('[Packages] Normalized:', packages);

        // Auto-create Trial package if not exists
        // Auto-create Default Packages
        let needsReload = false;

        // 1. Trial
        if (!packages.find(p => p.name.toLowerCase().includes('trial'))) {
            console.log('[Packages] Creating default Trial package...');
            await api.createPackage({
                name: 'Trial',
                description: '14-day Free Trial',
                price: 0,
                type: 'Free',
                billing_interval: 'monthly',
                features: ['Basic Protection', '2 Agents', '14 Days Retention'],
                duration: '14 Days'
            });
            needsReload = true;
        }

        // 2. Starter (Free)
        if (!packages.find(p => p.name.toLowerCase() === 'starter')) {
            console.log('[Packages] Creating default Starter package...');
            await api.createPackage({
                name: 'Starter',
                description: 'Essential security for small teams',
                price: 0,
                type: 'Free',
                billing_interval: 'monthly',
                features: ['Basic Threat Monitoring', 'Unlimited Agents', '3-Day Log Retention'],
                duration: 'Monthly'
            });
            needsReload = true;
        }

        // 3. Pro Systems (Paid)
        if (!packages.find(p => p.name.toLowerCase().includes('pro systems'))) {
            console.log('[Packages] Creating default Pro Systems package...');
            await api.createPackage({
                name: 'Pro Systems',
                description: 'Advanced protection for growing business',
                price: 120000,
                type: 'Paid',
                billing_interval: 'monthly',
                features: ['Real-time Threat Blocking', 'Unlimited Log Retention', 'Automated Remediation', 'Priority 24/7 Support'],
                duration: 'Monthly',
                is_recommended: true
            });
            needsReload = true;
        }

        if (needsReload) {
            return loadPackages();
        }

        allPackages = packages; // Store for toggling
        updatePackagesUI(packages);
    } catch (error) {
        console.error('[Packages] Failed to load:', error);
        showNotification('Failed to load packages', 'error');
    }
}

let currentInterval = 'monthly';
let allPackages = [];

function toggleBilling(interval) {
    currentInterval = interval;

    // Update button styles
    const monthlyBtn = document.getElementById('billing-monthly');
    const yearlyBtn = document.getElementById('billing-yearly');

    if (monthlyBtn && yearlyBtn) {
        if (interval === 'monthly') {
            monthlyBtn.className = 'px-6 py-2 rounded text-xs font-bold bg-neonCyan text-black uppercase transition-all';
            yearlyBtn.className = 'px-6 py-2 rounded text-xs font-bold text-slate-500 hover:text-white uppercase transition-all relative';
        } else {
            yearlyBtn.className = 'px-6 py-2 rounded text-xs font-bold bg-neonCyan text-black uppercase transition-all relative';
            monthlyBtn.className = 'px-6 py-2 rounded text-xs font-bold text-slate-500 hover:text-white uppercase transition-all';
        }
    }

    updatePackagesUI(allPackages);
}

function updatePackagesUI(packages) {
    try {
        const container = document.querySelector('#packages-container');
        if (!container) return;

        // Sort packages: Trial first, then by price
        packages.sort((a, b) => {
            if (a.name.toLowerCase().includes('trial')) return -1;
            if (b.name.toLowerCase().includes('trial')) return 1;
            return a.price - b.price;
        });

        container.innerHTML = packages.map(pkg => {
            // Calculate price based on interval
            let displayPrice = pkg.price;
            let displayInterval = 'mo';

            if (currentInterval === 'yearly' && pkg.price > 0) {
                // "Save 1 Month" deal: Pay for 11 months, get 12
                displayPrice = pkg.price * 11;
                displayInterval = 'yr';
            }

            return `
            <div class="cyber-panel p-6 rounded">
                <h3 class="text-xl font-bold text-white mb-2">${pkg.name}</h3>
                <p class="text-slate-500 text-sm mb-4">${pkg.description || ''}</p>
                <div class="text-3xl font-bold text-neonCyan mb-4">
                    Rp ${displayPrice === 0 ? '0' : displayPrice.toLocaleString('id-ID')}<span class="text-sm text-slate-500">/${displayInterval}</span>
                </div>
                <div class="space-y-2 mb-4">
                    ${(pkg.features_list || []).map(f => `
                        <div class="flex items-center text-sm">
                            <i class="fas fa-check text-neonCyan mr-2"></i>
                            <span>${f}</span>
                        </div>
                    `).join('') || ''}
                </div>
                <div class="flex gap-2">
                    <button onclick="editPackage(${pkg.id})" class="flex-1 bg-slate-800 text-white py-2 rounded hover:bg-slate-700">
                        <i class="fas fa-edit mr-1"></i> Edit
                    </button>
                    <button onclick="deletePackage(${pkg.id})" class="px-4 bg-red-900 text-red-300 py-2 rounded hover:bg-red-800">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
        `}).join('');
    } catch (error) {
        console.error('[Packages] Error updating UI:', error);
    }
}

export async function deletePackage(id) {
    if (!confirm('Are you sure you want to delete this package?')) return;

    try {
        await api.deletePackage(id);
        showNotification('Package deleted successfully', 'success');
        loadPackages();
    } catch (error) {
        console.error('[Packages] Failed to delete:', error);
        showNotification('Failed to delete package: ' + error.message, 'error');
    }
}

export function editPackage(id) {
    const pkg = allPackages.find(p => p.id === id);
    if (!pkg) return;

    const modal = document.getElementById('editPackageModal');
    const form = document.getElementById('editPackageForm');

    if (modal && form) {
        form.id.value = pkg.id;
        form.name.value = pkg.name;
        form.description.value = pkg.description;
        form.price.value = pkg.price;
        // Convert features list to comma string
        form.features.value = (pkg.features_list || []).join(', ');

        modal.classList.remove('hidden');
    }
}

// Handle Form Submit
document.addEventListener('submit', async (e) => {
    if (e.target && e.target.id === 'editPackageForm') {
        e.preventDefault();
        const form = e.target;
        const id = form.id.value;

        const data = {
            name: form.name.value,
            description: form.description.value,
            price: parseFloat(form.price.value),
            features: form.features.value.split(',').map(f => f.trim()).filter(f => f),
            // Preserve other fields
            billing_interval: 'monthly'
        };

        try {
            await api.updatePackage(id, data);
            showNotification('Package updated successfully', 'success');
            document.getElementById('editPackageModal').classList.add('hidden');
            loadPackages();
        } catch (error) {
            console.error('[Packages] Update failed:', error);
            showNotification('Failed to update package', 'error');
        }
    }
});

window.editPackage = editPackage;
window.deletePackage = deletePackage;
window.loadPackages = loadPackages;
window.toggleBilling = toggleBilling;
